import { presentLegalAcceptance } from "../legal_acceptance.js"
import { Controller } from "@hotwired/stimulus"
import { withOfflineDownloadLock } from "../offline_download_lock.js"
import { cacheOfflineReader, pauseOfflinePdfs } from "../offline_pdf_access.js"
import { OfflinePdfDownloader } from "../offline_pdf_download.js"
import { OfflinePdfStore, offlineCopyExpired } from "../offline_pdf_store.js"
import { ensureOfflineServiceWorker } from "../offline_service_worker.js"

let activeDownload = null

export default class extends Controller {
  static targets = ["button", "status", "progress"]
  static values = {
    manifestUrl: String,
    userId: Number,
    editionId: Number,
    labels: Object
  }

  async connect() {
    this.readinessController?.abort()
    this.readinessController = new AbortController()
    const { signal } = this.readinessController
    this.store = this.createStore()
    this.downloader = this.createDownloader(this.store)
    this.render("unavailable")

    if (!this.store.supported({ write: true }) || typeof navigator.locks?.request !== "function") {
      this.render("unsupported")
      return
    }

    try {
      await this.store.setActiveUser(this.userIdValue)
      signal.throwIfAborted()
      const record = (await this.store.list(this.userIdValue)).find((candidate) => candidate.editionId === this.editionIdValue)
      signal.throwIfAborted()
      if (!record) return
      if (offlineCopyExpired(record)) {
        await this.store.delete(record)
        signal.throwIfAborted()
        this.render("expired")
        return
      }

      const complete = record.ready && await this.store.verifyComplete(record)
      signal.throwIfAborted()
      if (complete) {
        this.render("confirming")
        try {
          await ensureOfflineServiceWorker({ signal })
        } catch (error) {
          if (error.name === "AbortError") return
          console.warn("Offline PDF navigation is not ready", error)
          this.render(error.code === "unsupported" ? "unsupported" : "paused")
          return
        }
      }
      signal.throwIfAborted()
      this.render(complete ? "ready" : "paused")
    } catch (error) {
      if (signal.aborted) return
      console.warn("Offline PDF storage is unavailable", error)
      const store = this.store
      this.store = null
      this.downloader = null
      try {
        await store.closeDatabase()
      } catch (_error) {
        // The storage backend is already unusable, so cleanup remains best-effort.
      }
      this.render("unsupported")
    }
  }

  createStore() {
    return new OfflinePdfStore()
  }

  createDownloader(store) {
    return new OfflinePdfDownloader({ store })
  }

  disconnect() {
    this.readinessController?.abort()
    if (activeDownload === this) this.pause()
    this.store?.closeDatabase()
  }

  async toggle() {
    if (activeDownload === this) {
      this.pause()
      return
    }
    if (["ready", "unsupported", "expired"].includes(this.state)) return
    await this.start()
  }

  async start() {
    if (activeDownload && activeDownload !== this) return

    try {
      await withOfflineDownloadLock(() => this.download())
    } catch (error) {
      console.error("Unable to coordinate the offline PDF download", error)
      this.render("unsupported")
    }
  }

  async download() {
    if (activeDownload && activeDownload !== this) return

    this.abortController = new AbortController()
    activeDownload = this
    try {
      this.render("confirming")
      const inspection = await this.downloader.inspect(this.manifestUrlValue, { signal: this.abortController.signal })
      const size = this.formatBytes(inspection.manifest.edition.byte_size)
      if (!window.confirm(this.labelsValue.confirm.replace("%{size}", size))) {
        this.render("unavailable")
        return
      }

      this.render("downloading")
      await ensureOfflineServiceWorker({ signal: this.abortController.signal })
      const record = await this.downloader.download(this.manifestUrlValue, {
        inspection,
        signal: this.abortController.signal,
        onProgress: ({ completed, total }) => this.renderProgress(completed, total)
      })
      try {
        await cacheOfflineReader(record)
      } catch (error) {
        record.ready = false
        await this.store.put(record)
        throw error
      }
      this.render("ready")
      this.dispatch("ready", { detail: { editionId: record.editionId } })
    } catch (error) {
      if (error.code === "legal_acceptance_required") {
        await pauseOfflinePdfs(this.store, { userId: this.userIdValue }, error.acceptanceUrl)
        this.render("paused")
        presentLegalAcceptance(error)
      } else if (error.name === "AbortError" || error instanceof TypeError || !navigator.onLine) {
        this.render("paused")
      } else {
        if (error.code === "stale") await this.deleteEditionCopies()
        console.error("Unable to download PDF for offline reading", error)
        this.render(error.code === "unsupported" ? "unsupported" : "failed", error.code)
      }
    } finally {
      if (activeDownload === this) activeDownload = null
      this.abortController = null
    }
  }

  pause() {
    this.abortController?.abort()
    this.render("paused")
  }

  render(state, errorCode = null) {
    this.state = state
    this.buttonTarget.textContent = this.labelsValue[state]
    this.buttonTarget.disabled = ["confirming", "ready", "unsupported", "expired"].includes(state)
    this.buttonTarget.setAttribute("aria-busy", String(state === "confirming" || state === "downloading"))
    this.statusTarget.textContent = errorCode === "quota" ? this.labelsValue.quota : this.labelsValue[`${state}Help`] || ""
    if (state !== "downloading") this.progressTarget.textContent = ""
  }

  renderProgress(completed, total) {
    const percentage = total ? Math.round(completed / total * 100) : 0
    this.progressTarget.textContent = `${percentage}%`
  }

  formatBytes(bytes) {
    return new Intl.NumberFormat(document.documentElement.lang, {
      style: "unit",
      unit: "megabyte",
      maximumFractionDigits: 1
    }).format(bytes / 1024 / 1024)
  }

  async deleteEditionCopies() {
    const records = await this.store.list(this.userIdValue)
    for (const record of records) {
      if (record.editionId === this.editionIdValue) await this.store.delete(record)
    }
  }
}
